import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ProcesosService from '../../services/ProcesosService';
import './ProcesosMenu.css';

const ProcesosMenu = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [procesos, setProcesos] = useState([]);
  const [procesosTotal, setProcesosTotal] = useState(0);
  const [procesosUnread, setProcesosUnread] = useState(0);
  const [procesosPage, setProcesosPage] = useState(1);
  const [showProcesos, setShowProcesos] = useState(false);
  const [selectedProceso, setSelectedProceso] = useState(null);
  const intervalRef = useRef(null);

  const formatProcesoDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('es-AR');
  };

  const getProcesoStatus = (proceso) => {
    if (proceso?.error) {
      return { label: 'Error', className: 'error' };
    }
    if (proceso?.fechaFinalizacion) {
      return { label: 'Finalizado', className: 'finalizado' };
    }
    return { label: 'En proceso', className: 'en-proceso' };
  };

  const fetchProcesos = async (page = 1) => {
    if (authLoading || !user) return;
    try {
      const response = await ProcesosService.getProcesos({ page, pageSize: 10 });
      if (response?.ok) {
        setProcesos(response.procesos || []);
        setProcesosTotal(response.total || 0);
        setProcesosUnread(response.unreadCount || 0);
        setProcesosPage(response.page || page);
      }
    } catch (error) {
      console.error('Error al cargar procesos:', error);
    }
  };

  useEffect(() => {
    if (authLoading || !user) return;
    fetchProcesos(1);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => fetchProcesos(1), 60000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [authLoading, user]);

  const handleOpenProceso = async (proceso) => {
    setSelectedProceso(proceso);
    if (!proceso?.leido) {
      try {
        await ProcesosService.marcarLeido(proceso.id);
        setProcesos((prev) => prev.map((item) => (item.id === proceso.id ? { ...item, leido: true } : item)));
        setProcesosUnread((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error al marcar proceso como leído:', error);
      }
    }
  };

  return (
    <>
      <div className="procesos-menu">
        <button
          type="button"
          className="procesos-btn"
          onClick={() => setShowProcesos((prev) => !prev)}
          title="Solicitudes Iniciadas"
        >
          <i className="fas fa-tasks"></i>
          <span>Solicitudes Iniciadas</span>
          {procesosUnread > 0 && (
            <span className="procesos-badge">{procesosUnread}</span>
          )}
        </button>
        {showProcesos && (
          <div className="procesos-dropdown">
            <div className="procesos-dropdown-header">
              <span>Solicitudes recientes</span>
              <span className="procesos-total" title={`Página ${procesosPage}`}>{procesosTotal}</span>
            </div>
            <div className="procesos-list">
              {procesos.length === 0 && (
                <div className="procesos-empty">No hay solicitudes.</div>
              )}
              {procesos.map((proceso) => {
                const status = getProcesoStatus(proceso);
                return (
                  <button
                    key={proceso.id}
                    type="button"
                    className={`procesos-item ${proceso.leido ? '' : 'unread'}`}
                    onClick={() => handleOpenProceso(proceso)}
                  >
                    <div className="procesos-item-main">
                      <span className="procesos-item-title">{proceso.descripcion}</span>
                      <span className={`procesos-status ${status.className}`}>{status.label}</span>
                    </div>
                    <div className="procesos-item-sub">
                      <span>{formatProcesoDate(proceso.fechaCreacion)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {selectedProceso && (
        <div className="modal-overlay" onClick={() => setSelectedProceso(null)}>
          <div className="modal-content procesos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-tasks"></i>
                Detalle de la solicitud
              </h3>
              <button className="modal-close-btn" onClick={() => setSelectedProceso(null)} type="button">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="procesos-modal-summary">
                <div>
                  <span className="label">Descripción</span>
                  <span className="value">{selectedProceso.descripcion}</span>
                </div>
                <div>
                  <span className="label">Creado</span>
                  <span className="value">{formatProcesoDate(selectedProceso.fechaCreacion)}</span>
                </div>
                <div>
                  <span className="label">Finalizado</span>
                  <span className="value">{formatProcesoDate(selectedProceso.fechaFinalizacion)}</span>
                </div>
                <div>
                  <span className="label">Estado</span>
                  {(() => {
                    const status = getProcesoStatus(selectedProceso);
                    return <span className={`procesos-status ${status.className}`}>{status.label}</span>;
                  })()}
                </div>
              </div>
              {selectedProceso.error && (
                <div className="procesos-modal-error">
                  <strong>Error:</strong> {selectedProceso.error}
                </div>
              )}
              <div className="procesos-modal-body">
                <span className="label">Body</span>
                <pre>{selectedProceso.bodyMensaje}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProcesosMenu;
